import { useContext, useEffect, useMemo, useRef } from 'react';
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json';
import { setPolicyContext } from '../kanban/kanbanTransitionPolicy';
import { decoupledSave } from '../../services/api';
import { notify } from '../../notices';

/**
 * TaskTransitionBridge — makes `KanbanTransitionMenu` usable OUTSIDE the board.
 *
 * The menu is already generic in its rendering, but it has two attachments to
 * KanbanBoard that no YAML can replace:
 *
 *   1. it asks `getAllowedTargets()` — a MODULE-level policy that the board
 *      seeds through `setPolicyContext()`. Unseeded, the policy returns no
 *      target and the menu renders nothing at all;
 *   2. picking a target only dispatches a `kanbanTransitionRequest` DOM event.
 *      Somebody has to hear it and persist.
 *
 * This component is that somebody, and nothing else: it renders no markup.
 * Dropping it next to a list of tasks turns every `KanbanTransitionMenu` in
 * that list into a working control, with the SAME pre-filter as the board —
 * reachable ∩ role-permitted ∩ subtask-safe — because it reuses the very same
 * policy module rather than reimplementing it. The server (TransitionValidator
 * + group permissions) stays the final authority; this only hides moves it
 * would refuse.
 *
 * Roles are fetched here rather than declared as a page data source on purpose:
 * they depend on the tasks' project, which is only known once the tasks are
 * loaded — and reactive-json builds every blocking `additionalDataSource` URL
 * in the same tick, so a dependent URL would be built before that value exists.
 *
 * Props (reactive-json passes raw values — each is evaluated here):
 *   - tasks     : the loaded task collection (syncable objects).
 *   - taskTypes : the `task_type` collection, each carrying `data.workflow`.
 *   - tasksPath : where that collection lives, so the new state can be written
 *                 back in place (e.g. `~~.tasks`). No refetch: the row updates
 *                 instantly, and a task reaching a closed state leaves the
 *                 "open tasks" view on its own.
 */
export const TaskTransitionBridge = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const ev = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext });

  // Les deux collections sont mémoïsées : sans cela le repli `|| []` fabrique un
  // nouveau tableau à chaque rendu et l'effet de semis rejouerait sans fin.
  const rawTasks = ev(props.tasks);
  const rawTypes = ev(props.taskTypes);
  const tasks = useMemo(() => (Array.isArray(rawTasks) ? rawTasks : []), [rawTasks]);
  const taskTypes = useMemo(() => (Array.isArray(rawTypes) ? rawTypes : []), [rawTypes]);
  const tasksPath = String(props.tasksPath ?? 'tasks').replace(/^~~?\./, '');

  // The handler is registered once but must always see the CURRENT tasks: keep
  // them in a ref so a transition fired right after a refresh targets the row
  // that is actually on screen.
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  const rolesRef = useRef([]);

  const projectId = tasks.find((t) => t?.data?.projectId)?.data?.projectId ?? null;

  // The user's cumulative group roles in the tasks' project. Refetched only
  // when the project changes, never per row.
  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    fetch(`/api/project/${projectId}/role`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled || !body) return;
        rolesRef.current = Array.isArray(body.roles) ? body.roles : [];
        setPolicyContext({ taskTypes, userRoles: rolesRef.current, allTasks: tasksRef.current });
      })
      .catch(() => { /* no roles → the menu offers nothing, which is the safe default */ });
    return () => { cancelled = true; };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-seed whenever the types or the task set change, so the menu never offers
  // a move against a stale workflow or a stale state.
  useEffect(() => {
    setPolicyContext({ taskTypes, userRoles: rolesRef.current, allTasks: tasks });
  }, [taskTypes, tasks]);

  useEffect(() => {
    const handler = (e) => {
      const { taskId, toState } = e.detail || {};
      if (!taskId || !toState) return;
      const index = (tasksRef.current || []).findIndex((t) => String(t?.data?.id) === String(taskId));
      const task = index >= 0 ? tasksRef.current[index] : null;
      if (!task || task.data.state === toState) return;

      // Write through the SAME display the row was read with, so the echo the
      // server returns matches the shape the list already holds.
      decoupledSave('node', 'task', { ...task.data, state: toState }, taskId, { display: 'decoupled_card' })
        .then(() => {
          globalDataContext.updateData(toState, `${tasksPath}.${index}.data.state`);
        })
        .catch((err) => {
          // The server refuses a move the pre-filter could not know about (the
          // subtask-closure guard returns 422). Say why instead of failing mute.
          notify({ type: 'error', message: String(err?.message || 'Transition refusée.') });
        });
    };
    document.addEventListener('kanbanTransitionRequest', handler);
    return () => document.removeEventListener('kanbanTransitionRequest', handler);
  }, [tasksPath, globalDataContext]);

  return null;
};

export default TaskTransitionBridge;
